import { s, Shape } from "../shapes"
import { HttpApi } from "./api";
import { Http } from "./http";

/**
 * Utility type. Maybe it should be moved?
 */
type OptionalShapeTypeOf<T extends (Shape | undefined | null)> =
  (undefined | null) extends T
    ? null // shape is empty
  : Shape extends T
    ? unknown // just a generic "shape"
  : Shape.Type.Of<Extract<T, Shape>>

export type Endpoint =
  | Endpoint.JSON
  | Endpoint.Raw

/**
 * An "Endpoint" is some RPC system, likely a HTTP or WebSocket
 * route definition.
 */
export namespace Endpoint {
  /**
   * Utilities for creating HTTP route endpoints of various types.
   */
  export namespace HttpUtils {
    export type Request<T extends Spec> = {
      query: T["query"],
      body: T["requestBody"],
      headers: T["requestHeaders"],
    };
    export type Response<T extends Spec> = {
      status: T["status"],
      body: T["responseBody"],
      headers: T["responseHeaders"],
    }
    /**
     * A version of "Request" where elements that aren't required
     * don't appear as "null" types, but are instead completely optional
     */
    export type LooseRequest<T extends Spec> =
      & (T["query"] extends (null | undefined)
        ? {} : { query: T["query"] })
      & (T["requestBody"] extends (null | undefined)
        ? {} : { body: T["requestBody"] })
      & { headers?: Record<string, string | void | string[]> }
    export type LooseResponse<T extends Spec> =
      & (number extends T["status"]
        ? { status?: number } : { status: T["status"] })
      & (T["responseBody"] extends (null | undefined)
        ? {} : { body: T["responseBody"] })
      & { headers?: Record<string, string | void | string[]> }

    /**
     * Every HTTP-based needs to define the following properties
     */
    export type Spec = {
      query: unknown,

      requestBody: unknown,
      requestHeaders: unknown,
      responseBody: unknown,
      responseHeaders: unknown,

      status: number,
    }

    /**
     * Define an EndpointImplementation for a given HttpUtils.Spec
     */
    export type HttpImplementation<T extends Spec> = DefineImplementation<{
      client: (request: LooseRequest<T>) => Promise<Response<T>>;
      server: (request: Request<T>, raw: Http.Request) => Promise<LooseResponse<T>>;
    }>
  }

  /**
   * An "endpoint implementation" is a pair of concrete types
   * that are used with the Endpoint - the "client" is something
   * that is _returned_ from Endpoint functions, for calling the
   * remote system. Inversely, the "server" type needs to be provided
   * to the Endpoint functions as an argument.
   */
  export type ImplementationBase = {
    client: unknown,
    server: unknown,
  }
  export type DefineImplementation<T extends ImplementationBase> = T;

  export type Implementation<T extends Endpoint> =
    T["type"] extends "json"
      ? JSON.JSONImplementation<Extract<T, JSON>>
    : T["type"] extends "raw"
      ? Raw.RawImplementation<Extract<T, Raw>>
    : ImplementationBase;


  export type JSON = JSON.Def;
  export namespace JSON {
    export type JSONImplementation<T extends Def> = HttpUtils.HttpImplementation<JSONDefToHTTPSpec<T>>

    export type Def = {
      type: 'json',
      query?: Shape | null,
      requestBody?: Shape | null,
      responseBody?: Shape | null,
    }
    
    export type JSONDefToHTTPSpec<T extends Def> = {
      query: OptionalShapeTypeOf<T["query"]>

      requestBody: OptionalShapeTypeOf<T["requestBody"]>,
      requestHeaders: Record<string, string>,
      responseBody: OptionalShapeTypeOf<T["responseBody"]>,
      responseHeaders: Record<string, string>,

      status: number,
    }
  }

  export type Raw = Raw.Def;
  export namespace Raw {
    export type Def = {
      type: 'raw',
      query?: Shape | null,
      
      requestStream?: boolean,
      responseStream?: boolean,
    }

    export type RawDefToHttpSpec<T extends Def> = {
      query: OptionalShapeTypeOf<T["query"]>

      requestBody: T["requestStream"] extends true ? ReadableStream<Uint8Array<ArrayBuffer>> : null,
      requestHeaders: Record<string, string>,
      responseBody: T["responseStream"] extends true ? null | ReadableStream<Uint8Array<ArrayBuffer>> : null,
      responseHeaders: Record<string, string>,

      status: number,
    }

    export type RawImplementation<T extends Def> = HttpUtils.HttpImplementation<RawDefToHttpSpec<T>>
  }

  export namespace Websocket {

  }
}

const JSONEndpoint = {
  def<const T extends Omit<Endpoint.JSON, "type">>(def: T) {
    return { ...def, type: 'json' } satisfies Endpoint.JSON;
  },
  client<const T extends Endpoint.JSON>(
    def: T,
    url: URL, method: string,
    client: Http.Client
  ): Endpoint.JSON.JSONImplementation<T>["client"] {
    return async (request) => {
      const finalURL = new URL(url);
      const query = (request as any).query;
      const requestBody = (request as any).body;

      if (def.query && query)
        for (const [queryKey, queryValue] of Object.entries(query))
          finalURL.searchParams.set(queryKey, queryValue as string);

      let requestBodyStream = null;
      if (def.requestBody && requestBody) {
        requestBodyStream = new Blob([JSON.stringify(requestBody)]).stream();
      }
      const rawResponse = await client.request(finalURL, {
        method,
        headers: request.headers,
        body: requestBodyStream,
      })
      let decodedResponse = null;
      if (rawResponse.body && def.responseBody) {
        decodedResponse = await new Response(rawResponse.body).json();
      }
      return {
        status: rawResponse.status,
        headers: rawResponse.headers,
        body: decodedResponse
      }
    }
  },
  server<const T extends Endpoint.JSON>(
    def: T,
    path: string,
    method: string,
    handler: Endpoint.JSON.JSONImplementation<T>["server"]
  ): HttpApi.Route {
    return { type: 'http', http: {
      method,
      path,
      async handler(rawRequest) {
        let decodedRequest = null;
        if (rawRequest.body && def.responseBody) {
          decodedRequest = await new Response(rawRequest.body).json();
        }

        const response = await handler({
          headers: rawRequest.headers,
          query: rawRequest.query as any,
          body: decodedRequest,
        }, rawRequest);
        const responseBody = (response as any).body;

        let responseStream = null;
        if (responseBody && def.responseBody) {
          responseStream = new Blob([JSON.stringify(responseBody)]).stream();
        }

        return {
          status: response.status || 200,
          headers: response.headers || {},
          body: responseStream
        }
      }
    } }
  }
}

const RawEndpoint = {
  def<const T extends Omit<Endpoint.Raw, "type">>(def: T) {
    return { ...def, type: 'raw' } satisfies Endpoint.Raw;
  },
  client<const T extends Endpoint.Raw>(
    def: T,
    url: URL, method: string,
    client: Http.Client
  ): Endpoint.Raw.RawImplementation<T>["client"] {
    return async (request) => {
      const finalURL = new URL(url);
      const query = (request as any).query;
      const requestBody: null | ReadableStream<Uint8Array> = (request as any).body;

      if (def.query && query)
        for (const [queryKey, queryValue] of Object.entries(query))
          finalURL.searchParams.set(queryKey, queryValue as string);

      const rawResponse = await client.request(finalURL, {
        method,
        headers: request.headers,
        body: requestBody,
      })

      return {
        status: rawResponse.status,
        headers: rawResponse.headers,
        body: rawResponse.body as any
      }
    }
  },
  server<const T extends Endpoint.Raw>(
    def: T,
    path: string,
    method: string,
    handler: Endpoint.Raw.RawImplementation<T>["server"]
  ): HttpApi.Route {
    return { type: 'http', http: {
      method,
      path,
      async handler(rawRequest) {
        const response = await handler({
          headers: rawRequest.headers,
          query: rawRequest.query as any,
          body: rawRequest.body as any,
        }, rawRequest);
        const responseBody = (response as any).body;

        return {
          status: response.status || 200,
          headers: response.headers || {},
          body: responseBody
        }
      }
    } }
  }
}

export const Endpoint = {
  Json: JSONEndpoint.def,
  Raw<const T extends Omit<Endpoint.Raw, "type">>(def: T) { return { ...def, type: "raw" } satisfies Endpoint.Raw },

  client<T extends Endpoint>(
    def: T,
    url: URL, method: string,
    client: Http.Client
  ): Endpoint.Implementation<T>["client"] {
    switch (def.type) {
      case 'json':
        type JSONDef = Extract<T, Endpoint.JSON>;
        const jsonDef = def as JSONDef;
        return JSONEndpoint.client(jsonDef, url, method, client) as Endpoint.JSON.JSONImplementation<JSONDef>["client"];
      case 'raw':
        type RawDef = Extract<T, Endpoint.Raw>;
        const rawDef = def as RawDef;
        return RawEndpoint.client(rawDef, url, method, client) as Endpoint.Raw.RawImplementation<RawDef>["client"];
      default:
        throw new Error(`Unsupported endpoint type`);
    }
  },
  server<T extends Endpoint>(
    def: T,
    path: string,
    method: string,
    handler: Endpoint.Implementation<T>["server"]
  ): HttpApi.Route {
    switch (def.type) {
      case 'json':
        type JSONDef = Extract<T, Endpoint.JSON.Def>;
        const jsonDef = def as JSONDef;
        const jsonHandler = handler as Endpoint.JSON.JSONImplementation<JSONDef>["server"];
        return JSONEndpoint.server(jsonDef, path, method, jsonHandler);
      case 'raw':
        type RawDef = Extract<T, Endpoint.Raw>;
        const rawDef = def as RawDef;
        const rawHandler = handler as Endpoint.Raw.RawImplementation<RawDef>["server"];
        return RawEndpoint.server(rawDef, path, method, rawHandler);
      default:
        throw new Error(`Unsupported endpoint type`);
    }
  },
} as const;
